import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Inject active hotel context (from cookie) into every request so hotel-scoped
// endpoints (Master Data, etc.) filter to the currently selected hotel.
api.interceptors.request.use((config) => {
  try {
    const c = document.cookie
      .split(";")
      .map((x) => x.trim())
      .find((x) => x.startsWith("active_hotel_id="));
    const v = c ? decodeURIComponent(c.split("=")[1]) : null;
    if (v && !config.headers?.["X-Hocp-Hotel"]) {
      config.headers = config.headers || {};
      config.headers["X-Hocp-Hotel"] = v;
    }
  } catch (_) { /* no-op */ }
  return config;
});

// Format FastAPI error responses (which may be arrays of {msg,...})
export function formatApiError(err) {
  const data = err?.response?.data;
  const detail = data?.detail ?? (data && typeof data === "object" && data.code && data.message ? data : undefined);
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object") {
    // Structured detail — e.g. { decision: "BLOCK", reason: "...", rule_code: "..." }
    // Prefer the human-readable fields; fall back to a JSON dump so nothing
    // is ever displayed as "[object Object]".
    return (
      detail.reason ||
      detail.msg ||
      detail.message ||
      detail.error ||
      JSON.stringify(detail)
    );
  }
  return String(detail);
}

// Try refresh on 401, retry once
let isRefreshing = false;
let queue = [];
const resolveQueue = (err) => {
  queue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  queue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original.__retried && !original.url.includes("/auth/")) {
      original.__retried = true;
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve: () => resolve(api(original)), reject });
        });
      }
      isRefreshing = true;
      try {
        await api.post("/auth/refresh");
        resolveQueue();
        return api(original);
      } catch (e) {
        resolveQueue(e);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default api;
